<script>
import { computed } from 'vue';

import useSectionState from '@/state/useSectionState';
import useUserState from '@/state/useUserState';
import useCalendarViewState from '@/state/useCalendarViewState';
import useMainState from '@/state/useMainState';
import {
  compsetsFilterConfig,
  sectionFiltersConfig,
} from '@/config/sectionFiltersConfig';
import handleDownloadReport from '@/utils/handleDownloadReport';
import DownloadSelect from '@/components/DownloadSelect/DownloadSelect.vue';

import useFilterState from '@/state/useFilterState';

import CompsetsFilter from '@/modules/DataSections/components/CompsetsFilter/CompsetsFilter.vue';

export default {
  components: {
    DownloadSelect,
    CompsetsFilter,
  },
  setup() {
    const {
      setCompsetsFilter,
      isChartsView,
      isTablesView,
      isCalendarView,
      sectionMetrics,
      isSectionDataLoading,
      isBreakdownOverviewSection,
      isFreeCalendarView,
    } = useSectionState();

    const { selectedMetric } = useFilterState();

    const { isOverview } = useMainState();

    const {
      selectedCalendarFirstCompset,
      selectedCalendarSecondCompset,
      pipeData: calendarPipeData,
      availableFirstCompsets,
      availableSecondCompsets,
    } = useCalendarViewState();
    const { isSoftBrandUser } = useUserState();

    const metricInfo = computed(() => sectionMetrics.value
      .filter((m) => m.name === selectedMetric.value)[0].desc);

    const firstCalendarSelectorOptions = computed(() => availableFirstCompsets.value
      .map((c) => ({
        label: c,
        value: c,
      })));

    const secondCalendarSelectorOptions = computed(() => availableSecondCompsets.value
      .map((c) => ({
        label: c,
        value: c,
      })));

    const showCompsetFilter = computed(() => sectionFiltersConfig.value.active
      && sectionFiltersConfig.value.compsets
      && compsetsFilterConfig.value);

    const isCalendarSelectorDisabled = computed(() => isFreeCalendarView.value
      || calendarPipeData.value.loading);

    const compsetsFilterTabs = computed(() => [
      {
        label: 'Default',
        value: 'default',
        show: true,
      },
      {
        label: 'Custom Compsets',
        value: 'custom',
        show: !isOverview.value && !isSoftBrandUser.value,
      },
      {
        label: 'Destinations',
        value: 'destination',
        show: !isOverview.value && !isSoftBrandUser.value,
      },
      {
        label: 'Brands',
        value: 'brand',
        show: isOverview.value,
      },
      {
        label: 'Clusters',
        value: 'cluster',
        show: true,
      },
    ].filter(({ show }) => show));

    const downloadOptions = computed(() => {
      const tmpOptions = [];
      if (isBreakdownOverviewSection.value) {
        return tmpOptions;
      }

      if (isTablesView.value) {
        tmpOptions.push({ label: 'CSV', tag: 'csv' });
      } else if (isChartsView.value) {
        tmpOptions.push({ label: 'PDF', tag: 'pdf' });
      }

      return tmpOptions;
    });

    return {
      setCompsetsFilter,
      compsetsFilterConfig,
      compsetsFilterTabs,
      sectionFiltersConfig,
      isChartsView,
      isCalendarView,
      isOverview,
      firstCalendarSelectorOptions,
      secondCalendarSelectorOptions,
      isCalendarSelectorDisabled,
      selectedCalendarFirstCompset,
      selectedCalendarSecondCompset,
      handleDownloadReport,
      metricInfo,
      calendarPipeData,
      isSectionDataLoading,
      showCompsetFilter,
      downloadOptions,
      isFreeCalendarView,
    };
  },
};
</script>

<template>
  <div
    :class="[
      'relative flex items-center z-80 mt-4',
      { 'justify-between': !!metricInfo },
      { 'justify-end': !metricInfo },
    ]"
  >
    <div
      v-if="metricInfo"
      data-testid="metric-info"
      class="pr-4 text-md text-base-700"
    >
      {{ metricInfo }}
    </div>

    <div class="flex items-center gap-4 filters">
      <template v-if="compsetsFilterConfig && showCompsetFilter && !isCalendarView">
        <CompsetsFilter
          data-testid="compsets-filter"
          :config="compsetsFilterConfig"
          :tabs="compsetsFilterTabs"
        />
      </template>

      <slot name="customActions" />

      <template v-if="isCalendarView">
        <div class="space-y-4 text-md text-base-900">
          {{ $t('Compare') }}
        </div>

        <LpSelect
          v-model="selectedCalendarFirstCompset"
          :options="firstCalendarSelectorOptions"
          :disabled="isCalendarSelectorDisabled || !firstCalendarSelectorOptions.length"
          menu-width="14rem"
          x-position="right"
          size="medium"
          :placeholder="$t('Select')"
        />

        <div class="space-y-4 text-md text-base-900">
          {{ $t('to') }}
        </div>

        <LpSelect
          v-model="selectedCalendarSecondCompset"
          :options="secondCalendarSelectorOptions"
          :disabled="isCalendarSelectorDisabled || !secondCalendarSelectorOptions.length"
          menu-width="14rem"
          x-position="right"
          size="medium"
          :placeholder="$t('Select')"
        />
      </template>

      <template
        v-else
      >
        <div
          v-if="showCompsetFilter"
          class="h-full"
        >
          <span class="filter-separator" />
        </div>
        <DownloadSelect
          v-if="downloadOptions.length"
          :options="downloadOptions"
          :disabled="isSectionDataLoading"
        />
      </template>
    </div>
  </div>
</template>
